/**
 * Structured Logging Utility
 *
 * Provides centralized, structured logging with log levels and PII protection.
 * Replaces console.log with proper logging infrastructure.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4,
}

interface LogContext {
  component?: string
  userId?: string
  organizationId?: string
  requestId?: string
  [key: string]: any
}

interface LogEntry {
  timestamp: string
  level: string
  message: string
  context?: LogContext
  error?: {
    message: string
    stack?: string
    code?: string
  }
}

class Logger {
  private minLevel: LogLevel
  private isDevelopment: boolean
  private isTest: boolean

  constructor() {
    this.isDevelopment = import.meta.env.MODE === 'development'
    this.isTest = import.meta.env.MODE === 'test'

    // Set minimum log level based on environment
    const envLevel = import.meta.env['VITE_LOG_LEVEL']?.toUpperCase()
    this.minLevel = this.parseLogLevel(envLevel) ?? (this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO)
  }

  private parseLogLevel(level?: string): LogLevel | null {
    if (!level) return null

    const levelMap: Record<string, LogLevel> = {
      'DEBUG': LogLevel.DEBUG,
      'INFO': LogLevel.INFO,
      'WARN': LogLevel.WARN,
      'ERROR': LogLevel.ERROR,
      'SILENT': LogLevel.SILENT,
    }

    return levelMap[level] ?? null
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel
  }

  private formatEntry(level: string, message: string, context?: LogContext, error?: Error): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
    }

    if (context && Object.keys(context).length > 0) {
      entry.context = this.sanitizeContext(context)
    }

    if (error) {
      entry.error = {
        message: error.message,
        stack: this.isDevelopment ? error.stack : undefined,
        code: (error as any).code,
      }
    }

    return entry
  }

  private sanitizeContext(context: LogContext): LogContext {
    const sanitized = { ...context }

    // Mask sensitive fields
    const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'authorization']

    for (const key of Object.keys(sanitized)) {
      const lowerKey = key.toLowerCase()

      // Mask sensitive keys
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]'
      }

      // Mask full email addresses (keep domain for debugging)
      if (typeof sanitized[key] === 'string' && sanitized[key].includes('@')) {
        const email = sanitized[key]
        const [localPart, domain] = email.split('@')
        if (localPart && domain) {
          sanitized[key] = `${localPart.substring(0, 2)}***@${domain}`
        }
      }

      // Truncate IDs for readability (keep last 8 chars)
      if ((key.endsWith('Id') || key.endsWith('_id')) && typeof sanitized[key] === 'string') {
        const id = sanitized[key]
        if (id.length > 12) {
          sanitized[key] = `...${id.slice(-8)}`
        }
      }
    }

    return sanitized
  }

  private output(entry: LogEntry): void {
    if (this.isTest) {
      // Suppress logs during tests unless explicitly enabled
      return
    }

    const { level, message, context, error } = entry

    // Format output based on environment
    if (this.isDevelopment) {
      // Pretty print for development
      const prefix = `[${level}] ${message}`

      switch (level) {
        case 'DEBUG':
          console.debug(prefix, context || '', error || '')
          break
        case 'INFO':
          console.info(prefix, context || '', error || '')
          break
        case 'WARN':
          console.warn(prefix, context || '', error || '')
          break
        case 'ERROR':
          console.error(prefix, context || '', error || '')
          break
      }
    } else {
      // Structured JSON for production (can be sent to logging service)
      console.log(JSON.stringify(entry))
    }
  }

  /**
   * Debug logging - only shown in development
   * Use for detailed debugging information
   */
  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return

    const entry = this.formatEntry('DEBUG', message, context)
    this.output(entry)
  }

  /**
   * Info logging - general informational messages
   * Use for normal application flow events
   */
  info(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.INFO)) return

    const entry = this.formatEntry('INFO', message, context)
    this.output(entry)
  }

  /**
   * Warning logging - potentially harmful situations
   * Use for deprecated features, poor API usage, etc.
   */
  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.WARN)) return

    const entry = this.formatEntry('WARN', message, context)
    this.output(entry)
  }

  /**
   * Error logging - error events
   * Use for exceptions and error conditions
   */
  error(message: string, error?: Error, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.ERROR)) return

    const entry = this.formatEntry('ERROR', message, context, error)
    this.output(entry)
  }

  /**
   * Create a child logger with preset context
   * Useful for component-specific logging
   */
  child(childContext: LogContext): Logger {
    const childLogger = new Logger()
    const parentDebug = this.debug.bind(this)
    const parentInfo = this.info.bind(this)
    const parentWarn = this.warn.bind(this)
    const parentError = this.error.bind(this)

    childLogger.debug = (message: string, context?: LogContext) => {
      parentDebug(message, { ...childContext, ...context })
    }

    childLogger.info = (message: string, context?: LogContext) => {
      parentInfo(message, { ...childContext, ...context })
    }

    childLogger.warn = (message: string, context?: LogContext) => {
      parentWarn(message, { ...childContext, ...context })
    }

    childLogger.error = (message: string, error?: Error, context?: LogContext) => {
      parentError(message, error, { ...childContext, ...context })
    }

    return childLogger
  }
}

// Export singleton instance
export const logger = new Logger()

// Export convenience function for creating component loggers
export function createLogger(component: string): Logger {
  return logger.child({ component })
}
